import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { Button } from '@connected/ui/components/button';
import { ContentLayout } from '@connected/ui/layouts/content/layout';
import { createFileRoute, Link, useMatch, useSearch } from '@tanstack/react-router';
import { ArrowLeft, CircleCheck, Lock } from 'lucide-react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

type FormValues = {
    password: string;
    repeatPassword: string;
};

export const Route = createFileRoute('/_unauth/reset-password/')({
    component: PasswordResetRouteComponent,
    validateSearch: z.object({
        UserId: z.string().optional(),
        Code: z.string().optional(),
    }),
});

const DEFAULT_VALUES: FormValues = {
    password: '',
    repeatPassword: '',
};

export function PasswordResetRouteComponent() {
    const isPasswordResetPage = useMatch({ from: '/_unauth/reset-password/', shouldThrow: false });
    const search = useSearch({ from: isPasswordResetPage ? '/_unauth/reset-password/' : '/_unauth/account/activate/' });
    const { t } = useTranslation('unauth');
    const [postSuccess, setPostSuccess] = useState(false);
    const navigate = Route.useNavigate();

    const mutation = api.useMutation(
        'post',
        isPasswordResetPage ? '/api/v2/accounts/passwords/reset' : '/api/v2/accounts/activate',
        {
            onSuccess: async () => {
                setPostSuccess(true);
            },
            onError: () => {
                toast.error(t('reset-password.pending.error.general'));
            },
        },
    );

    const form = useAppForm({
        defaultValues: DEFAULT_VALUES,
        onSubmit: ({ value }) => {
            if (!postSuccess) {
                if (!search.UserId || !search.Code) {
                    return toast.error(
                        t('reset-password.pending.error.invalid_link'),
                    );
                }

                mutation.mutate({
                    body: {
                        UserId: search.UserId,
                        ...(isPasswordResetPage
                            ? { ResetCode: search.Code }
                            : { ActivateCode: search.Code }
                        ),
                        Password: value.password,
                    },
                });
            }
            else {
                navigate({ to: '/' });
            }
        },
    });

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    return (
        <ContentLayout className="h-full w-full items-center justify-center gap-3 max-w-none">
            <ContentLayout.Header className="w-full max-w-sm">
                <div className="flex justify-center">
                    <div className="border border-solid rounded-xl w-fit p-3 m-3">
                        {!postSuccess ? <Lock /> : <CircleCheck />}
                    </div>
                </div>
                <ContentLayout.Title className="text-center text-3xl pb-1">
                    {!postSuccess
                        ? t('reset-password.pending.title')
                        : t('reset-password.success.title')}
                </ContentLayout.Title>
                {(isPasswordResetPage || (!isPasswordResetPage && postSuccess)) && (
                    <ContentLayout.SubTitle className="text-center text-base font-normal px-3 pb-2">
                        {!postSuccess
                            ? t('reset-password.pending.description')
                            : <Trans>{isPasswordResetPage ? t('reset-password.success.description') : t('activate-account.success.description')}</Trans>}
                    </ContentLayout.SubTitle>
                )}
            </ContentLayout.Header>
            <form className="w-full max-w-sm flex flex-col gap-4 p-3 pt-0" onSubmit={handleSubmit}>
                <form.AppForm>
                    {!postSuccess && (
                        <>
                            <form.AppField
                                name="password"
                                validators={{
                                    onSubmit:
                                        validators.activateAccountV2Request.password,
                                }}
                            >
                                {field => (
                                    <field.Label>
                                        <field.Label.Title label={t('reset-password.fields.password.title')} />
                                        <field.Input
                                            type="password"
                                            placeholder={t(
                                                'reset-password.fields.password.placeholder',
                                            )}
                                        />
                                        <field.ValidationError />
                                    </field.Label>
                                )}
                            </form.AppField>

                            <form.AppField
                                name="repeatPassword"
                                validators={{
                                    onSubmit: z
                                        .string()
                                        .refine(
                                            value => value === form.getFieldValue('password'),
                                            t('reset-password.fields.password_confirm.error-not-identical'),
                                        ),
                                }}
                            >
                                {field => (
                                    <field.Label>
                                        <field.Label.Title label={t('reset-password.fields.password_confirm.title')} />
                                        <field.Input
                                            type="password"
                                            placeholder={t('reset-password.fields.password_confirm.placeholder')}
                                        />
                                        <field.ValidationError />
                                    </field.Label>
                                )}
                            </form.AppField>

                            <form.Subscribe selector={state => state.values.password}>
                                {(value) => {
                                    const minCharacters = value.length >= 10;
                                    const minSpecialCharacters = /[!@#$%^&*(),.?":{}|<>]/.test(value);

                                    return (
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center justify-start gap-2 mt-2">
                                                <CircleCheck
                                                    style={{
                                                        stroke: 'hsl(var(--background))',
                                                        fill: minCharacters
                                                            ? '#32c770'
                                                            : 'hsl(var(--border))',
                                                    }}
                                                />
                                                {t('reset-password.pending.password-requirements.min-characters')}
                                            </div>

                                            <div className="flex items-center justify-start gap-2 mt-2">
                                                <CircleCheck
                                                    style={{
                                                        stroke: 'hsl(var(--background))',
                                                        fill: minSpecialCharacters
                                                            ? '#32c770'
                                                            : 'hsl(var(--border))',
                                                    }}
                                                />
                                                {t('reset-password.pending.password-requirements.min-special-characters')}
                                            </div>
                                        </div>
                                    );
                                }}
                            </form.Subscribe>
                        </>
                    )}

                    <form.SubmitButton asChild>
                        <Button
                            type="submit"
                            className="mt-2 text-base"
                            disabled={mutation.isPending}
                        >
                            {!postSuccess
                                ? isPasswordResetPage ? t('reset-password.pending.button') : t('activate-account.pending.button')
                                : t('reset-password.success.button')}
                        </Button>
                    </form.SubmitButton>
                </form.AppForm>
                {!postSuccess && (
                    <div className="flex flex-col">
                        <Button
                            variant="link"
                            className="font-semibold hover:no-underline text-foreground"
                            asChild
                        >
                            <Link to="/login">
                                <ArrowLeft />
                                {' '}
                                {t('reset-password.back-to-login')}
                            </Link>
                        </Button>
                    </div>
                )}
            </form>
        </ContentLayout>
    );
}
